<template>
    <vx-navbar
        v-if="!hiddenForCurrentPath"
        :key="currentPath"
        :responsiveOffset="FEATURES.contact_groups ? -80 : 20"
        class="app-navbar mb-6 md:mb-0"
    >
        <template v-slot:left>
            <vx-navbar-action href="/" :active="isHome()" class="hide-action-icon" @click="navigate('/', $event)">
                <navbar-logo></navbar-logo>
            </vx-navbar-action>

            <vx-button
                v-if="windowWidth > 768 && canUseMessageHub"
                class="ml-6 mr-4"
                href="/message-hub"
                color="primary"
                size="sm"
                @click="navigate('/message-hub', $event)"
            >
                <font-awesome-icon widthAuto class="text-sm" :icon="faMessages"></font-awesome-icon>
                Message Hub
            </vx-button>

            <vx-button
                v-if="windowWidth > 768 && showQuickBlastButton"
                href="/messaging/quick-blast"
                color="info"
                size="sm"
                :class="{
                    'ml-6 mr-4': !canUseMessageHub,
                }"
                @click="navigate('/messaging/quick-blast', $event)"
            >
                <font-awesome-icon widthAuto class="text-sm" :icon="faRocketLaunch"></font-awesome-icon>
                Quick Blast
            </vx-button>
        </template>

        <template v-if="windowWidth <= 768" v-slot:nested-before>
            <vx-button
                v-if="canUseMessageHub"
                block
                href="/message-hub"
                color="primary"
                size="sm"
                @click="navigate('/message-hub', $event)"
            >
                <font-awesome-icon widthAuto class="text-sm" :icon="faMessages"></font-awesome-icon>
                Message Hub
            </vx-button>
            <vx-button
                v-if="showQuickBlastButton"
                block
                href="/messaging/quick-blast"
                color="info"
                size="sm"
                @click="navigate('/messaging/quick-blast', $event)"
            >
                <font-awesome-icon widthAuto class="text-sm" :icon="faRocketLaunch"></font-awesome-icon>
                Quick Blast
            </vx-button>
        </template>

        <vx-navbar-action
            v-for="action in routes"
            :key="action.name"
            :icon="action.icon"
            :href="action.href"
            :target="action.target"
            :rel="action.target ? 'noopener noreferrer' : undefined"
            :active="isActive(action.pathMatcher)"
            :data-test="`navbar-section-${testId(action.name)}`"
            @click="handleLinkClick(action, $event)"
        >
            {{ action.name }}

            <template v-if="action.items?.length" v-slot:items>
                <vx-navbar-action-item
                    v-for="item in action.items"
                    :key="`${action.name}-${item.name}`"
                    :href="item.href"
                    :target="item.target"
                    :rel="item.target ? 'noopener noreferrer' : undefined"
                    :active="isActive(item.pathMatcher, item.href)"
                    :aria-current="isActive(item.pathMatcher, item.href) ? 'page' : undefined"
                    :data-test="`navbar-item-${testId(item.name)}`"
                    @click="handleLinkClick(item, $event)"
                >
                    {{ item.name }}
                </vx-navbar-action-item>
            </template>
        </vx-navbar-action>

        <template v-if="windowWidth <= 768" v-slot:nested-after>
            <navbar-settings :metadata="props.metadata"></navbar-settings>
        </template>

        <template v-slot:right>
            <vx-navbar-action v-if="windowWidth > 768" placement="right" class="ml-2" data-test="navbar-settings">
                <vx-button color="secondary" size="sm">
                    <font-awesome-icon widthAuto class="text-sm" :icon="faGear"></font-awesome-icon>
                </vx-button>

                <template v-slot:items>
                    <navbar-settings :metadata="props.metadata"></navbar-settings>
                </template>
            </vx-navbar-action>

            <vx-navbar-action v-if="FEATURES.contact_groups" v-model:open="groupsDropdown" placement="right" hideArrow>
                <navbar-context-button></navbar-context-button>
                <template v-slot:dropdown>
                    <navbar-context-switch></navbar-context-switch>
                </template>
            </vx-navbar-action>
        </template>
    </vx-navbar>

    <div v-if="!hiddenForCurrentPath" class="hidden pt-6 px-6 md:block sm:h-12 md:px-8 sm:mb-8">
        <vx-navigation v-if="activeActionItems.length">
            <vx-navigation-item
                v-for="item in activeActionItems"
                :key="`subnav-${item.name}`"
                :value="item.name"
                :href="item.href"
                :target="item.target"
                :rel="item.target ? 'noopener noreferrer' : undefined"
                :active="isActive(item.pathMatcher, item.href)"
                :aria-current="isActive(item.pathMatcher, item.href) ? 'page' : undefined"
                :data-test="`navbar-subnav-${testId(item.name)}`"
                @click="handleLinkClick(item, $event)"
            >
                {{ item.name }}
            </vx-navigation-item>
        </vx-navigation>
    </div>
</template>

<style>
.hide-action-icon > svg {
    display: none;
}
</style>

<script setup>
import {
    VxNavbar,
    VxNavbarAction,
    VxNavbarActionItem,
    VxButton,
    VxNavigation,
    VxNavigationItem,
} from '@voxie/frontend-components';
import { navbarRoutes } from './navbar-routes';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faGear, faMessages, faRocketLaunch } from '@fortawesome/pro-solid-svg-icons';
import NavbarLogo from './NavbarLogo.vue';
import NavbarSettings from './NavbarSettings.vue';
import NavbarContextSwitch from './NavbarContextSwitch.vue';
import NavbarContextButton from './NavbarContextButton.vue';
import { computed, onMounted, onBeforeUnmount, ref, nextTick, inject } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useRouterNavigation } from '~/composables/useRouterNavigation';
import { useEventListener } from '@vueuse/core';

const FEATURES = inject('FEATURES');

const { onCorporate, roleOnCurrentOrgUnit, roleOnCurrentGroup, canUseMessageHub } = useCurrentGroup();

const showQuickBlastButton = computed(
    () => onCorporate() || ['admin', 'owner', 'marketer', 'blast'].includes(roleOnCurrentOrgUnit || roleOnCurrentGroup)
);

const props = defineProps({
    metadata: {
        type: Object,
        default: () => ({
            isServiceRepresentative: false,
            showsTeamSwitcher: false,
            ownsCurrentTeam: false,
            isAdminRoleOnCurrentTeam: false,
            isImpersonator: false,
            isPlatformAdmin: false,
            hasSupportAddress: false,
        }),
    },
});

const windowWidth = ref(window.innerWidth);

const routes = navbarRoutes(props.metadata);

const { currentMeta, currentPath, navigate } = useRouterNavigation();

const hiddenForCurrentPath = computed(() => Boolean(currentMeta.value.hideAppNavbar));

const testId = (name) => name.toLowerCase().replace(/\s+/g, '-');

const isHome = () => {
    return currentPath.value === '/';
};

const isActive = (pathMatcher, href) => {
    if (!pathMatcher) {
        return href == currentPath.value;
    }
    return Boolean(pathMatcher(currentPath.value));
};

const handleLinkClick = (link, event) => {
    link.onClick?.();

    if (link.href) {
        navigate(link.href, event, link.target);
    }
};

const activeActionItems = computed(
    () => routes.find((action) => isActive(action.pathMatcher, action.href))?.items || []
);

const setWindowWidth = () => {
    windowWidth.value = window.innerWidth;
};

onMounted(() => {
    nextTick().then(setWindowWidth);
    window.addEventListener('resize', setWindowWidth);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setWindowWidth);
});

const groupsDropdown = ref(false);
useEventListener(window, 'keydown', (event) => {
    if ((event.metaKey && event.key === 'g') || (event.ctrlKey && event.key === 'g')) {
        event.preventDefault();
        groupsDropdown.value = !groupsDropdown.value;
    }
    if (event.key === 'Escape') {
        groupsDropdown.value = false;
    }
});
</script>
